import { useEffect, useState } from "react";
import api, { hrs, fmtDate } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { Clock, CalendarCheck, AlertCircle, Plane, Bell, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { ClockCard } from "@/components/ClockCard";

function Stat({ icon: Icon, label, value, to }) {
  const inner = (
    <div className="bg-white border border-zinc-200 rounded-md p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-md bg-slate-900 flex items-center justify-center">
        <Icon className="h-5 w-5 text-amber-500" />
      </div>
      <div className="flex-1">
        <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-500">{label}</div>
        <div className="num text-xl font-black text-zinc-950">{value}</div>
      </div>
      {to && <ChevronRight className="h-4 w-4 text-zinc-400" />}
    </div>
  );
  return to ? <Link to={to} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link> : inner;
}

export default function EmployeeHome() {
  const [d, setD] = useState(null);
  const load = () => api.get("/dashboard/employee").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return <EmployeeLayout title="Home"><div className="text-zinc-400 text-sm">Loading…</div></EmployeeLayout>;
  return (
    <EmployeeLayout title="Home">
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-950">This week</h1>
          <p className="text-sm text-zinc-500">Status: <StatusBadge status={d.week_status} /></p>
        </div>
        <Link to="/app/timesheets" data-testid="add-timesheet-cta" className="bg-amber-500 text-black font-bold px-4 py-2.5 rounded-md text-sm hover:bg-amber-600 transition-colors">
          + Add time
        </Link>
      </div>

      <ClockCard onChange={load} />

      <div className="grid gap-3">
        <Stat icon={Clock} label="Hours this week" value={`${hrs(d.hours_this_week)}h`} to="/app/timesheets" />
        <Stat icon={AlertCircle} label="Needs action" value={d.action_needed} to="/app/timesheets" />
        <Stat icon={Plane} label="Leave balance" value={`${d.leave_balance} days`} to="/app/leave" />
        <Stat icon={CalendarCheck} label="Pending requests" value={d.pending_leave} to="/app/leave" />
      </div>

      {d.upcoming_leave?.length > 0 && (
        <div className="mt-6">
          <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Upcoming approved leave</h2>
          <div className="space-y-2">
            {d.upcoming_leave.map((l) => (
              <div key={l.id} className="bg-white border border-zinc-200 rounded-md p-3 text-sm flex justify-between">
                <span>{l.leave_type}</span>
                <span className="num text-zinc-500">{fmtDate(l.start_date)} – {fmtDate(l.end_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> Notifications</h2>
        <div className="space-y-2">
          {d.notifications?.length === 0 && <div className="text-sm text-zinc-400">Nothing new.</div>}
          {d.notifications?.map((n) => (
            <div key={n.id} className="bg-white border border-zinc-200 rounded-md p-3 text-sm text-zinc-700">{n.message}</div>
          ))}
        </div>
      </div>
    </EmployeeLayout>
  );
}
